import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PDFExportButton } from "@/components/estimation-details/pdf-preview/PDFExportButton";
import { useAnalytics } from "@/analytics";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { EstimateType } from "@/enums/estimate-type";
import type { Estimation } from "@/interfaces/estimation.interface";
import { Archive, Check, Loader2, MoreHorizontal, Plus } from "lucide-react";

interface EstimateHeaderActionsProps {
  estimateId: string;
  estimation: Estimation | undefined;
  isUpdateLineItemLoading: boolean;
  isUpdateScopeNoteLoading: boolean;
  onAddNote: () => void;
  isPdfLoading: boolean;
  onPDFExport: (verbose?: boolean) => void;
  onPhotoReportExport: () => void;
  hasPdfExport: boolean;
  exportedToXactimate: boolean;
  isExporting: boolean;
  hasLineItems: boolean;
  onExportToXactimate: () => void;
  onGenerateRecon: () => void;
  onArchive: () => void;
}

export const EstimateHeaderActions = ({
  estimateId,
  estimation,
  isUpdateLineItemLoading,
  isUpdateScopeNoteLoading,
  onAddNote,
  isPdfLoading,
  onPDFExport,
  onPhotoReportExport,
  hasPdfExport,
  exportedToXactimate,
  isExporting,
  hasLineItems,
  onExportToXactimate,
  onGenerateRecon,
  onArchive,
}: EstimateHeaderActionsProps) => {
  const { trackEvent, ANALYTICS_EVENTS } = useAnalytics();
  const { isFeatureEnabled } = useFeatureFlags();

  const isMitToReconEnabled = isFeatureEnabled("MIT_TO_RECON");
  const isMoldToReconEnabled = isFeatureEnabled("MOLD_TO_RECON");
  const isPdfExportEnabled = isFeatureEnabled("PDF_EXPORT_ENABLED");
  const isPdfExportVerboseEnabled = isFeatureEnabled("PDF_EXPORT_VERBOSE");
  const isPhotoReportEnabled = isFeatureEnabled("RESPONSIVE_NOTE_CAMERA_CAPTURE");
  const showRecon =
    (estimation?.estimateType === EstimateType.MITIGATION && isMitToReconEnabled) ||
    (estimation?.estimateType === EstimateType.MOLD && isMoldToReconEnabled);
  const showXactimate = estimation?.estimateType !== EstimateType.TIME_AND_MATERIAL;
  const isXactimateExportDisabled = isExporting || !hasLineItems;
  const noLineItemsTooltip = "Add line items to this estimate before exporting to Xactimate";

  return (
    <div className="flex flex-row items-center space-x-2">
      {isUpdateLineItemLoading && <LoadingIcon />}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <Button
                data-testid="upload-additional-notes"
                variant="secondary"
                disabled={isUpdateScopeNoteLoading}
                onClick={() => {
                  trackEvent(ANALYTICS_EVENTS.ADDITIONAL_UPLOAD_STARTED, {
                    estimate_id: estimateId,
                    estimate_type: estimation?.estimateType,
                  });
                  onAddNote();
                }}
              >
                <Plus className="h-4 w-4" />
                Add note
              </Button>
            </span>
          </TooltipTrigger>
          {isUpdateScopeNoteLoading && (
            <TooltipContent>
              <p>Cannot upload new note while estimate is being created or updated</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      {showRecon && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  data-testid="generate-recon-estimate"
                  variant="default"
                  disabled={!hasLineItems}
                  onClick={() => {
                    trackEvent(ANALYTICS_EVENTS.MIT_TO_RECON_ESTIMATE_GENERATED, {
                      source_estimate_id: estimateId,
                    });
                    onGenerateRecon();
                  }}
                >
                  Generate recon estimate
                </Button>
              </span>
            </TooltipTrigger>
            {!hasLineItems && (
              <TooltipContent className="bg-background text-foreground">
                Add line items to this estimate before generating a recon estimate
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      <PDFExportButton
        isPdfExportEnabled={isPdfExportEnabled}
        isPdfExportVerboseEnabled={isPdfExportVerboseEnabled}
        isPdfLoading={isPdfLoading}
        handlePDFExport={onPDFExport}
        hasPdfExport={hasPdfExport}
        isPhotoReportEnabled={isPhotoReportEnabled}
        onPhotoReportExport={onPhotoReportExport}
      />
      {showXactimate &&
        (exportedToXactimate ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    data-testid="export-to-xactimate"
                    disabled={isXactimateExportDisabled}
                    onClick={() => {
                      trackEvent(ANALYTICS_EVENTS.XACTIMATE_EXPORT_INITIATED, {
                        estimate_id: estimateId,
                        estimate_type: estimation?.estimateType,
                        previously_exported: true,
                      });
                      onExportToXactimate();
                    }}
                    className="relative"
                    variant="accent"
                  >
                    {isExporting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Export to Xactimate
                    <span className="absolute -top-2 -right-2 flex items-center justify-center rounded-full bg-emerald-500 p-[2px] shadow-lg">
                      <Check size={8} className="text-white" />
                    </span>
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent className="bg-background text-foreground">
                {hasLineItems ? "Previously exported to Xactimate" : noLineItemsTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="accent"
                    data-testid="export-to-xactimate"
                    disabled={isXactimateExportDisabled}
                    onClick={() => {
                      trackEvent(ANALYTICS_EVENTS.XACTIMATE_EXPORT_INITIATED, {
                        estimate_id: estimateId,
                        estimate_type: estimation?.estimateType,
                        previously_exported: false,
                      });
                      onExportToXactimate();
                    }}
                  >
                    {isExporting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Export to Xactimate
                  </Button>
                </span>
              </TooltipTrigger>
              {!hasLineItems && <TooltipContent className="bg-background text-foreground">{noLineItemsTooltip}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon">
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="archive-project" className="text-destructive focus:text-destructive" onClick={onArchive}>
            <Archive className="h-4 w-4" />
            Archive project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const LoadingIcon = ({ size = 24 }: { size?: number }) => {
  return (
    <div className="flex px-2">
      <svg width={size} height={size} viewBox="0 0 24 24">
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="#d1d5db"
          strokeWidth="3"
          fill="none"
          strokeDasharray="31.4 31.4"
          strokeLinecap="round"
          style={{ animation: "spin 1s linear infinite", transformOrigin: "center" }}
        />
      </svg>
    </div>
  );
};
